import { IS_LOCAL } from '@/helpers';

const I18N = {
  ko: {
    dailyMissionDetail: {
      dateFormat: 'yy.LL.dd',
    },
  },
  en: {
    dailyMissionDetail: {
      dateFormat: 'LLL dd, yyyy',
    },
  },
};

const NewYearStore = {
  namespaced: true,
  state() {
    return {
      /** 데일리 미션 상태 */
      dailyMissionStatus: {
        attendance: 0,
        invite: 0,
        subs: 0,
        rentalIncome: 0,
        swap: 0,
      },
      /** 젓가락 상태 */
      chopstickStatus: {
        usable: 0,
        total: 0,
      },
      /** 데일리 미션 상세 */
      dailyMissionDetail: [],
      /** 누적 음식 세트 수 */
      totalDishSets: 0,
      /** 음식 세트 보상 수령 가능 여부 */
      dishRewardAvailable: [],
      /** 이벤트 종료 후 남은 젓가락 수 */
      remainingChopsticks: 0,
      /** 이벤트 종료 후 남은 젓가락 교환 가능 여부 */
      remainingChopsticksAvailable: false,
    };
  },
  getters: {
    getDailyMissionDetailSum: (state) => state.dailyMissionDetail.reduce((acc, item) => acc + item.value, 0),
  },
  mutations: {
    setDailyMissionStatus(state, status) {
      state.dailyMissionStatus = status;
    },
    setChopstickStatus(state, status) {
      state.chopstickStatus = status;
    },
    setDailyMissionDetail(state, detail) {
      state.dailyMissionDetail = detail;
    },
    setTotalDishSets(state, snack) {
      state.totalDishSets = snack;
    },
    setDishRewardAvailable(state, isAvailable) {
      state.dishRewardAvailable = isAvailable;
    },
    setRemainingChopsticks(state, { chopstick, isAvailable }) {
      state.remainingChopsticks = chopstick;
      state.remainingChopsticksAvailable = isAvailable;
    },
  },
  actions: {
    async fetch(_, { method, url, params }) {
      const data = method === 'get' ? { params: { ...params } } : params;
      const response = await this._vm.$axios[method](`${process.env.VUE_APP_TEST_SERVER}${url}`, data);
      return response;
    },
    /**
     * 데일리 미션 상태 조회
     * @returns {Promise<{
     *   attendance: number;
     *   invite: number;
     *   subs: number;
     *   rentalIncome: number;
     *   swap: number;
     * }>}
     */
    async getDailyMissionStatus({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: {
            attendance: 1,
            invite: 4,
            subs: 2,
            rentalIncome: 1,
            swap: 1,
          },
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/newyear/daily-mission',
      });
      return response;
    },
    /**
     * 젓가락 상태 조회
     * @returns {Promise<{
     *   usable: number;
     *   total: number;
     * }>}
     */
    async getChopstickStatus({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: {
            usable: 5,
            total: 4,
          },
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/newyear/chopsticks',
      });
      return response;
    },
    /**
     * 이벤트 1 상태 조회
     */
    async fetchEvent1Status({ dispatch, commit }) {
      const [{ data: dailyMissionStatus }, { data: chopstickStatus }] = await Promise.all([
        dispatch('getDailyMissionStatus'),
        dispatch('getChopstickStatus'),
      ]);

      commit('setDailyMissionStatus', dailyMissionStatus);
      commit('setChopstickStatus', chopstickStatus);
      return;
    },
    /**
     * 젓가락 상태 조회
     */
    async fetchChopstickStatus({ dispatch, commit }) {
      const { data: chopstickStatus } = await dispatch('getChopstickStatus');

      commit('setChopstickStatus', chopstickStatus);
      return;
    },
    /**
     * 데일리 미션 상세 조회
     * @returns {Promise<{
     *   date: string;
     *   mission: string;
     *   value: number;
     * }[]>}
     */
    async getDailyMissionDetail({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: [
            {
              date: '2023-01-01T00:00:00',
              mission: '출석 체크 완료',
              value: 1,
            },
            {
              date: '2023-01-02T00:00:00',
              mission: '출석 체크 완료',
              value: 4,
            },
          ],
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/newyear/daily-mission/detail',
      });
      return response;
    },
    /**
     * 데일리 미션 상세 조회
     */
    async fetchDailyMissionDetail({ dispatch, commit, rootState }) {
      const { data: dailyMissionDetail } = await dispatch('getDailyMissionDetail');
      const { lang } = rootState;

      const format = I18N[lang].dailyMissionDetail.dateFormat;
      const formattedDailyMissionDetail = dailyMissionDetail.map((item) => ({
        ...item,
        date: this._vm.$tl(item.date, lang).toFormat(format),
      }));

      commit('setDailyMissionDetail', formattedDailyMissionDetail);
      return;
    },
    /**
     * 음식 보상 받기
     * @returns {Promise<number>}
     * @example 1 // 0.5, 1, 2, 2.5, 5
     */
    async postChopstickReward({ dispatch }) {
      if (IS_LOCAL) {
        return { data: 1 };
      }

      const response = await dispatch('fetch', {
        method: 'post',
        url: '/newyear/chopsticks',
      });
      return response;
    },
    /**
     * 음식 보상 받기 후 완료 처리
     * @returns {Promise<number | Error & { message: { title: string, content: string } }>}
     * @example
     * // success
     * 1 // 0.5, 1, 2, 2.5, 5
     * // error
     * Error {
     *   "message": {
     *     "title": "음식 세트를 완성할 수 없습니다",
     *     "content": "완성하기 위한 젓가락가 모자랍니다. 미션을 통해 젓가락를 모아보세요."
     *   }
     * }
     */
    async submitChopstickReward({ dispatch }) {
      try {
        const { data: reward } = await dispatch('postChopstickReward');

        return reward;
      } catch (error) {
        if (!error.response?.data?.message) throw error;

        return error.response.data.message;
      } finally {
        dispatch('fetchChopstickStatus');
        dispatch('fetchDishSetStatus');
        dispatch('fetchRemainingChopsticks');
      }
    },
    /**
     * 누적 음식 세트 상태 조회
     * @returns {Promise<{
     *   snack: number;
     *   isAvailable: boolean[];
     * }>}
     */
    async getDishSetStatus({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: {
            food: 8,
            isAvailable: [false, true, false, false],
          },
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/newyear/food',
      });
      return response;
    },
    /**
     * 누적 음식 세트 상태 조회
     */
    async fetchDishSetStatus({ dispatch, commit }) {
      const {
        data: { food, isAvailable },
      } = await dispatch('getDishSetStatus');

      commit('setTotalDishSets', food);
      commit('setDishRewardAvailable', isAvailable);
      return;
    },
    /**
     * 음식 보상 받기
     */
    async postDishReward({ dispatch }, index) {
      if (IS_LOCAL) {
        return;
      }

      await dispatch('fetch', {
        method: 'post',
        url: '/newyear/food',
        params: {
          index,
        },
      });

      return;
    },
    /**
     * 음식 보상 받기
     */
    async submitDishReward({ dispatch }, index) {
      try {
        await dispatch('postDishReward', index);

        return true;
      } catch (error) {
        if (!error.response?.data?.message) throw error;

        return error.response.data.message;
      } finally {
        dispatch('fetchDishSetStatus');
      }
    },
    /**
     * 이벤트 종료 후 남은 젓가락 수량 조회
     * @returns {Promise<{
     *   chopstick: number; // 남은 젓가락 수
     *   isAvailable: boolean; // true: 교환가능할 때
     * }>}
     */
    async getRemainingChopsticks({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: {
            chopsticks: 4,
            isAvailable: true,
          },
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/newyear/change/chopsticks',
      });
      return response;
    },
    /**
     * 이벤트 종료 후 남은 젓가락 수량 업데이트
     */
    async fetchRemainingChopsticks({ dispatch, commit }) {
      const {
        data: { chopsticks, isAvailable },
      } = await dispatch('getRemainingChopsticks');

      commit('setRemainingChopsticks', { chopstick: chopsticks, isAvailable });
      return;
    },
    /**
     * 이벤트 3 교환 신청
     * @returns {Promise<boolean | {
     *   message: {
     *     title: string;
     *     content: string;
     *   };
     * }>}
     */
    async postExchange({ dispatch }) {
      if (IS_LOCAL) {
        return true;
      }

      const response = await dispatch('fetch', {
        method: 'post',
        url: '/newyear/change/chopsticks',
      });
      return response;
    },
    /**
     * 이벤트 3 교환 신청
     */
    async submitExchange({ dispatch }) {
      try {
        await dispatch('postExchange');

        return true;
      } catch (error) {
        if (!error.response?.data?.message) throw error;

        return error.response.data.message;
      } finally {
        dispatch('fetchChopstickStatus');
        dispatch('fetchRemainingChopsticks');
      }
    },
  },
};

export default NewYearStore;
